import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';
const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';

const BULK_MAX_ITEMS = 20;
const BULK_MAX_IMAGE_BYTES = 350 * 1024;
const BULK_MAX_TOTAL_DATAURL_CHARS = 7_500_000;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const statusRef = useRef(null);
  const [username, setUsername] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const [showProductSettings, setShowProductSettings] = useState(false);
  const [productSettingsLoading, setProductSettingsLoading] = useState(false);
  const [multiQuantityEnabled, setMultiQuantityEnabled] = useState(false);
  const [maxQuantityAllowed, setMaxQuantityAllowed] = useState(10);
  const [autoInventoryUpdateEnabled, setAutoInventoryUpdateEnabled] = useState(true);

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState('');

  const [selectedProductIds, setSelectedProductIds] = useState(() => new Set());
  const [bulkManageBusy, setBulkManageBusy] = useState(false);
  const [bulkManageError, setBulkManageError] = useState('');
  const [showBulkManageModal, setShowBulkManageModal] = useState(false);
  const [bulkManageFields, setBulkManageFields] = useState({ Category: '', Price: '', Stock: '' });
  const [bulkSalePercent, setBulkSalePercent] = useState('');
  const [bulkSaleMode, setBulkSaleMode] = useState('percent'); // percent | price
  const [bulkSalePrice, setBulkSalePrice] = useState('');
  const [bulkManageMode, setBulkManageMode] = useState('update'); // update | sale | clear_sale

  const [formData, setFormData] = useState({
    ProductName: '',
    Description: '',
    Price: '',
    Category: '',
    Stock: 0,
    ImageURL: '',
    AllImages: []
  });

  const [saleEnabled, setSaleEnabled] = useState(false);
  const [saleMode, setSaleMode] = useState('percent'); // percent | price
  const [salePercent, setSalePercent] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [syncSquareEnabled, setSyncSquareEnabled] = useState(false);
  const [squareSyncBusy, setSquareSyncBusy] = useState(false);

  const [imagePreview, setImagePreview] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [imageFileName, setImageFileName] = useState('');
  
  // AltImages state - separate existing (from server) and new (user uploads)
  const [existingAltImages, setExistingAltImages] = useState([]); // URLs from server
  const [altImageFiles, setAltImageFiles] = useState([]); // New file objects
  const [altImagePreviews, setAltImagePreviews] = useState([]); // Combined previews
  const [altImageDataUrls, setAltImageDataUrls] = useState([]); // New uploads only
  const [themeCategories, setThemeCategories] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const categories = useMemo(() => {
    const unique = new Set(
      themeCategories
        .map((cat) => (typeof cat === 'string' ? cat.trim() : ''))
        .filter(Boolean)
    );
    products.forEach((product) => {
      if (product?.Category) {
        unique.add(product.Category.trim());
      }
    });
    return Array.from(unique)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [products, themeCategories]);

  const visibleProducts = selectedCategory
    ? products.filter((p) => p.Category === selectedCategory)
    : products;

  useEffect(() => {
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncSquareCatalog = async () => {
    if (!API_BASE_URL) return;
    if (squareSyncBusy) return;
    setSquareSyncBusy(true);
    setError('');
    setSuccessMessage('');
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_BASE_URL}/admin/pos/catalog/sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || `Failed to sync Square catalog (${response.status})`);
      }
      const processed = data?.processed ?? data?.synced;
      const failures = Array.isArray(data?.failures) ? data.failures : [];
      const summary = failures.length
        ? `Square sync complete: ${processed || 0} processed, ${failures.length} failures.`
        : `Square sync complete: ${processed || 0} processed.`;
      setSuccessMessage(summary);
      fetchProducts();
    } catch (e) {
      console.error('Square catalog sync error:', e);
      setError(e?.message || 'Failed to sync Square catalog');
    } finally {
      setSquareSyncBusy(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!successMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [successMessage, error]);

  const clearOnKeyDown = (e, onClear) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    e.preventDefault();
    if (typeof onClear === 'function') onClear();
  };

  const submitBulkManageAction = async () => {
    if (bulkManageMode === 'sale') {
      return applyBulkSaleToSelected();
    }
    if (bulkManageMode === 'clear_sale') {
      return clearBulkSaleForSelected();
    }
    return submitBulkUpdateSelected();
  };

  const applyBulkSaleToSelected = async () => {
    setBulkManageError('');
    setError('');
    setSuccessMessage('');

    const ids = Array.from(selectedProductIds);
    if (ids.length === 0) {
      setBulkManageError('Select at least one product.');
      return;
    }

    const mode = bulkSaleMode === 'price' ? 'price' : 'percent';
    const pctNum = Number(String(bulkSalePercent || '').trim());
    const salePriceInput = Number(String(bulkSalePrice || '').trim());
    if (mode === 'percent') {
      if (!Number.isFinite(pctNum) || pctNum <= 0 || pctNum > 95) {
        setBulkManageError('Percent off must be between 1 and 95.');
        return;
      }
    } else {
      if (!Number.isFinite(salePriceInput) || salePriceInput <= 0) {
        setBulkManageError('Sale price must be a number greater than 0.');
        return;
      }
    }

    const toNumberOrNull = (value) => {
      if (value === null || typeof value === 'undefined') return null;
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      const raw = String(value || '').trim();
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };

    const round2 = (n) => Math.round(n * 100) / 100;

    const isOnSale = (product) => {
      const priceNum = toNumberOrNull(product?.Price);
      const compareNum = toNumberOrNull(product?.CompareAtPrice);
      return priceNum !== null && compareNum !== null && compareNum > 0 && compareNum > priceNum;
    };

    setBulkManageBusy(true);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      let okCount = 0;
      let skipCount = 0;
      const failures = [];

      const categoryVal = (bulkManageFields.Category || '').trim();
      const stockRaw = bulkManageFields.Stock;
      let stockNum = null;
      if (stockRaw !== '' && stockRaw !== null && typeof stockRaw !== 'undefined') {
        const parsed = parseInt(stockRaw, 10);
        if (!Number.isNaN(parsed) && parsed >= 1) stockNum = parsed;
      }

      for (const productId of ids) {
        const product = products.find((p) => String(p?.ProductID) === String(productId));
        if (!product) {
          failures.push(`Product not found: ${productId}`);
          continue;
        }

        if (isOnSale(product)) {
          skipCount += 1;
          continue;
        }

        const basePrice = toNumberOrNull(product?.Price);
        if (basePrice === null || basePrice <= 0) {
          failures.push(`Invalid price for ${productId}`);
          continue;
        }

        let salePrice = null;
        if (mode === 'percent') {
          salePrice = round2(basePrice * (1 - pctNum / 100));
          if (!(salePrice > 0 && salePrice < basePrice)) {
            failures.push(`Computed sale price invalid for ${productId}`);
            continue;
          }
        } else {
          salePrice = round2(salePriceInput);
          if (!(salePrice > 0 && salePrice < basePrice)) {
            failures.push(`Sale price must be less than current price for ${productId}`);
            continue;
          }
        }

        const payload = {
          Price: String(salePrice),
          CompareAtPrice: String(basePrice),
        };
        if (categoryVal) payload.Category = categoryVal;
        if (stockNum !== null) payload.Stock = stockNum;

        try {
          const resp = await fetch(`${API_BASE_URL}/admin/products/${productId}`, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

          const json = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            failures.push(json?.error || `Failed (${resp.status})`);
          } else {
            okCount += 1;
          }
        } catch (innerErr) {
          failures.push(innerErr?.message || 'Failed');
        }
      }

      const total = ids.length;
      if (okCount > 0 || skipCount > 0) {
        setSuccessMessage(`Sale applied: ${okCount}/${total} updated (skipped ${skipCount} already on sale).`);
      }
      if (failures.length > 0) {
        setBulkManageError(`Some updates failed (${failures.length}/${total}). Check console/logs for details.`);
        console.error('Bulk sale failures:', failures);
      }
      closeBulkManage();
      clearSelectedProducts();
      fetchProducts();
    } catch (err) {
      console.error('Bulk sale error:', err);
      setBulkManageError(err?.message || 'Bulk sale failed');
    } finally {
      setBulkManageBusy(false);
    }
  };

  const clearBulkSaleForSelected = async () => {
    setBulkManageError('');
    setError('');
    setSuccessMessage('');

    const ids = Array.from(selectedProductIds);
    if (ids.length === 0) {
      setBulkManageError('Select at least one product.');
      return;
    }

    const toNumberOrNull = (value) => {
      if (value === null || typeof value === 'undefined') return null;
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      const raw = String(value || '').trim();
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };

    const isOnSale = (product) => {
      const priceNum = toNumberOrNull(product?.Price);
      const compareNum = toNumberOrNull(product?.CompareAtPrice);
      return priceNum !== null && compareNum !== null && compareNum > 0 && compareNum > priceNum;
    };

    setBulkManageBusy(true);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      let okCount = 0;
      let skipCount = 0;
      const failures = [];

      const categoryVal = (bulkManageFields.Category || '').trim();
      const stockRaw = bulkManageFields.Stock;
      let stockNum = null;
      if (stockRaw !== '' && stockRaw !== null && typeof stockRaw !== 'undefined') {
        const parsed = parseInt(stockRaw, 10);
        if (!Number.isNaN(parsed) && parsed >= 1) stockNum = parsed;
      }

      for (const productId of ids) {
        const product = products.find((p) => String(p?.ProductID) === String(productId));
        if (!product) {
          failures.push(`Product not found: ${productId}`);
          continue;
        }

        if (!isOnSale(product)) {
          skipCount += 1;
          continue;
        }

        const restorePrice = toNumberOrNull(product?.CompareAtPrice);
        if (restorePrice === null || restorePrice <= 0) {
          failures.push(`Missing CompareAtPrice for ${productId}`);
          continue;
        }

        const payload = {
          Price: String(restorePrice),
          CompareAtPrice: null,
        };
        if (categoryVal) payload.Category = categoryVal;
        if (stockNum !== null) payload.Stock = stockNum;

        try {
          const resp = await fetch(`${API_BASE_URL}/admin/products/${productId}`, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

          const json = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            failures.push(json?.error || `Failed (${resp.status})`);
          } else {
            okCount += 1;
          }
        } catch (innerErr) {
          failures.push(innerErr?.message || 'Failed');
        }
      }

      const total = ids.length;
      if (okCount > 0 || skipCount > 0) {
        setSuccessMessage(`Sale cleared: ${okCount}/${total} updated (skipped ${skipCount} not on sale).`);
      }
      if (failures.length > 0) {
        setBulkManageError(`Some updates failed (${failures.length}/${total}). Check console/logs for details.`);
        console.error('Bulk clear sale failures:', failures);
      }
      closeBulkManage();
      clearSelectedProducts();
      fetchProducts();
    } catch (err) {
      console.error('Bulk clear sale error:', err);
      setBulkManageError(err?.message || 'Bulk clear sale failed');
    } finally {
      setBulkManageBusy(false);
    }
  };

  const _readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || '');
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });

  const _extractImageTypeFromDataUrl = (dataUrl) => {
    if (typeof dataUrl !== 'string') return 'jpg';
    const match = dataUrl.match(/^data:image\/(.+);base64,/);
    const ext = match && match[1] ? match[1] : 'jpg';
    if (ext === 'jpeg') return 'jpg';
    return ext;
  };

  const _rgbToColorName = (r, g, b) => {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    const v = max;
    const s = max === 0 ? 0 : delta / max;

    if (v < 40) return 'Black';
    if (s < 0.12 && v > 220) return 'White';
    if (s < 0.12) return 'Gray';

    let h = 0;
    if (delta === 0) h = 0;
    else if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;

    if (h < 15 || h >= 345) return 'Red';
    if (h < 45) return 'Orange';
    if (h < 70) return 'Yellow';
    if (h < 160) return 'Green';
    if (h < 250) return 'Blue';
    if (h < 290) return 'Purple';
    if (h < 345) return 'Pink';
    return 'Colorful';
  };

  const _colorDistance = (r1, g1, b1, r2, g2, b2) => {
    const dr = r1 - r2;
    const dg = g1 - g2;
    const db = b1 - b2;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  };

  const _estimateBackgroundRgb = (data, size) => {
    const samplePoints = [
      [2, 2],
      [size - 3, 2],
      [2, size - 3],
      [size - 3, size - 3],
      [Math.floor(size / 2), 2],
      [Math.floor(size / 2), size - 3],
    ];
    const samples = [];
    for (const [x, y] of samplePoints) {
      const idx = (y * size + x) * 4;
      const a = data[idx + 3];
      if (a < 10) continue;
      samples.push([data[idx], data[idx + 1], data[idx + 2]]);
    }
    if (samples.length === 0) return { r: 245, g: 245, b: 245 };
    samples.sort((a, b) => (a[0] + a[1] + a[2]) - (b[0] + b[1] + b[2]));
    const mid = samples[Math.floor(samples.length / 2)];
    return { r: mid[0], g: mid[1], b: mid[2] };
  };



  const _rgbToHsv = (r, g, b) => {
    const rr = r / 255;
    const gg = g / 255;
    const bb = b / 255;
    const max = Math.max(rr, gg, bb);
    const min = Math.min(rr, gg, bb);
    const delta = max - min;
    let h = 0;
    if (delta === 0) h = 0;
    else if (max === rr) h = ((gg - bb) / delta) % 6;
    else if (max === gg) h = (bb - rr) / delta + 2;
    else h = (rr - gg) / delta + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
    const s = max === 0 ? 0 : delta / max;
    const v = max;
    return { h, s, v };
  };

  const _hsvToColorName = (h, s, v) => {
    if (v < 0.12) return 'Black';
    if (s < 0.14 && v > 0.88) return 'White';
    if (s < 0.14) return 'Gray';

    const isDark = v < 0.55;

    if (h < 15 || h >= 345) return isDark ? 'Burgundy' : 'Red';
    if (h < 45) return isDark ? 'Brown' : 'Orange';
    if (h < 70) return isDark ? 'Gold' : 'Yellow';
    if (h < 160) return isDark ? 'Olive' : 'Green';
    if (h < 250) return isDark ? 'Navy' : 'Blue';
    if (h < 290) return isDark ? 'Plum' : 'Purple';
    if (h < 345) return 'Pink';
    return 'Colorful';
  };

  const _computeDominantColorName = async (file) => {
    if (!file) return '';
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const size = 64;
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) {
            resolve('');
            URL.revokeObjectURL(url);
            return;
          }
          const sw = img.naturalWidth || img.width || 0;
          const sh = img.naturalHeight || img.height || 0;
          const cropW = sw ? Math.round(sw * 0.75) : 0;
          const cropH = sh ? Math.round(sh * 0.75) : 0;
          const sx = sw ? Math.round((sw - cropW) / 2) : 0;
          const sy = sh ? Math.round((sh - cropH) / 2) : 0;

          if (sw && sh && cropW > 0 && cropH > 0) {
            ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, size, size);
          } else {
            ctx.drawImage(img, 0, 0, size, size);
          }

          const data = ctx.getImageData(0, 0, size, size).data;
          const bg = _estimateBackgroundRgb(data, size);
          const binSize = 15;
          const bins = Math.ceil(360 / binSize);
          const weights = new Array(bins).fill(0);
          const vTotals = new Array(bins).fill(0);
          const sTotals = new Array(bins).fill(0);
          const counts = new Array(bins).fill(0);

          const grid = 8;
          const cellSize = Math.max(1, Math.floor(size / grid));
          const cellWeights = new Array(grid * grid).fill(0);
          const cellCounts = new Array(grid * grid).fill(0);

          let fallbackR = 0;
          let fallbackG = 0;
          let fallbackB = 0;
          let fallbackCount = 0;

          for (let i = 0; i < data.length; i += 16) {
            const pixelIndex = i / 4;
            const x = pixelIndex % size;
            const y = Math.floor(pixelIndex / size);
            const a = data[i + 3];
            if (a < 10) continue;
            const rr = data[i];
            const gg = data[i + 1];
            const bb = data[i + 2];
            const max = Math.max(rr, gg, bb);
            const min = Math.min(rr, gg, bb);
            const delta = max - min;

            // Ignore near-white and near-black and low-saturation grays; usually background.
            if (max > 245) continue;
            if (max < 25) continue;
            if (delta < 10 && max > 210) continue;
            if (_colorDistance(rr, gg, bb, bg.r, bg.g, bg.b) < 26) continue;

            fallbackR += rr;
            fallbackG += gg;
            fallbackB += bb;
            fallbackCount++;

            const hsv = _rgbToHsv(rr, gg, bb);
            if (hsv.s < 0.18) continue;

            const cellX = Math.min(grid - 1, Math.floor(x / cellSize));
            const cellY = Math.min(grid - 1, Math.floor(y / cellSize));
            const cellIdx = cellY * grid + cellX;
            const cw = hsv.s * hsv.v;
            cellWeights[cellIdx] += cw;
            cellCounts[cellIdx] += 1;

            const idx = Math.max(0, Math.min(bins - 1, Math.floor(hsv.h / binSize)));
            const w = hsv.s * hsv.v;
            weights[idx] += w;
            vTotals[idx] += hsv.v;
            sTotals[idx] += hsv.s;
            counts[idx] += 1;
          }

          // Focus on the densest colored region so props/background don't dominate.
          let bestCell = 0;
          for (let i = 0; i < cellWeights.length; i++) {
            if (cellCounts[i] < 4) continue;
            if (cellWeights[i] > cellWeights[bestCell]) bestCell = i;
          }
          const bestCellX = bestCell % grid;
          const bestCellY = Math.floor(bestCell / grid);

          const regionWeights = new Array(bins).fill(0);
          const regionVTotals = new Array(bins).fill(0);
          const regionSTotals = new Array(bins).fill(0);
          const regionCounts = new Array(bins).fill(0);

          for (let i = 0; i < data.length; i += 16) {
            const pixelIndex = i / 4;
            const x = pixelIndex % size;
            const y = Math.floor(pixelIndex / size);
            const cellX = Math.min(grid - 1, Math.floor(x / cellSize));
            const cellY = Math.min(grid - 1, Math.floor(y / cellSize));
            if (Math.abs(cellX - bestCellX) > 1 || Math.abs(cellY - bestCellY) > 1) continue;

            const a = data[i + 3];
            if (a < 10) continue;
            const rr = data[i];
            const gg = data[i + 1];
            const bb = data[i + 2];
            const max = Math.max(rr, gg, bb);
            const min = Math.min(rr, gg, bb);
            const delta = max - min;

            if (max > 245) continue;
            if (max < 25) continue;
            if (delta < 10 && max > 210) continue;
            if (_colorDistance(rr, gg, bb, bg.r, bg.g, bg.b) < 26) continue;

            const hsv = _rgbToHsv(rr, gg, bb);
            if (hsv.s < 0.18) continue;

            const idx = Math.max(0, Math.min(bins - 1, Math.floor(hsv.h / binSize)));
            const w = hsv.s * hsv.v;
            regionWeights[idx] += w;
            regionVTotals[idx] += hsv.v;
            regionSTotals[idx] += hsv.s;
            regionCounts[idx] += 1;
          }

          const regionTotalWeight = regionWeights.reduce((sum, x) => sum + x, 0);
          const totalWeight = weights.reduce((sum, x) => sum + x, 0);
          if (regionTotalWeight <= 0 && totalWeight <= 0) {
            resolve('');
          } else {
            const useRegion = regionTotalWeight >= totalWeight * 0.45;
            const activeWeights = useRegion ? regionWeights : weights;
            const activeCounts = useRegion ? regionCounts : counts;
            const activeVTotals = useRegion ? regionVTotals : vTotals;
            const activeSTotals = useRegion ? regionSTotals : sTotals;

            let bestIdx = 0;
            let secondIdx = -1;
            for (let i = 0; i < activeWeights.length; i++) {
              if (activeWeights[i] > activeWeights[bestIdx]) {
                secondIdx = bestIdx;
                bestIdx = i;
              } else if (i !== bestIdx && (secondIdx < 0 || activeWeights[i] > activeWeights[secondIdx])) {
                secondIdx = i;
              }
            }

            const bestH = bestIdx * binSize + binSize / 2;
            const bestV = activeCounts[bestIdx] ? activeVTotals[bestIdx] / activeCounts[bestIdx] : 0.7;
            const bestS = activeCounts[bestIdx] ? activeSTotals[bestIdx] / activeCounts[bestIdx] : 0.6;
            const primary = _hsvToColorName(bestH, bestS, bestV);

            if (secondIdx >= 0 && activeWeights[secondIdx] >= activeWeights[bestIdx] * 0.55) {
              const secondH = secondIdx * binSize + binSize / 2;
              const secondV = activeCounts[secondIdx] ? activeVTotals[secondIdx] / activeCounts[secondIdx] : 0.7;
              const secondS = activeCounts[secondIdx] ? activeSTotals[secondIdx] / activeCounts[secondIdx] : 0.6;
              const secondary = _hsvToColorName(secondH, secondS, secondV);
              if (secondary && secondary !== primary) {
                resolve(`${primary} and ${secondary}`);
                return;
              }
            }

            if (primary) {
              resolve(primary);
            } else if (fallbackCount > 0) {
              resolve(
                _rgbToColorName(
                  Math.round(fallbackR / fallbackCount),
                  Math.round(fallbackG / fallbackCount),
                  Math.round(fallbackB / fallbackCount)
                )
              );
            } else {
              resolve('');
            }
          }
        } catch (_) {
          resolve('');
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {
          // ignore
        }
        resolve('');
      };
      img.src = url;
    });
  };

  const _validateBulkRow = (row) => {
    const errors = {};
    const name = (row.ProductName || '').trim();
    const description = (row.Description || '').trim();
    const category = (row.Category || '').trim();
    const price = row.Price;
    const stock = row.Stock;

    if (!name) errors.ProductName = 'Required';
    if (!description) errors.Description = 'Required';
    if (!category) errors.Category = 'Required';

    const priceNum = Number(price);
    if (!price && price !== 0) {
      errors.Price = 'Required';
    } else if (Number.isNaN(priceNum) || priceNum <= 0) {
      errors.Price = 'Must be > 0';
    }

    const stockNum = parseInt(stock, 10);
    if (Number.isNaN(stockNum) || stockNum < 1) {
      errors.Stock = 'Must be ≥ 1';
    }

    if (!row.imageDataUrl) errors.Image = 'Image missing';

    return errors;
  };

  const openBulkUpload = () => {
    setBulkError('');
    setBulkRows([]);
    setBulkCategory('');
    setShowBulkModal(true);
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const clearSelectedProducts = () => {
    setSelectedProductIds(new Set());
  };

  const selectAllVisibleProducts = () => {
    setSelectedProductIds(new Set(visibleProducts.map((p) => p.ProductID)));
  };

  const openBulkManage = () => {
    setBulkManageError('');
    setBulkManageFields({ Category: '', Price: '', Stock: '' });
    setBulkSalePercent('');
    setBulkSaleMode('percent');
    setBulkSalePrice('');
    setBulkManageMode('update');
    setShowBulkManageModal(true);
  };

  const closeBulkManage = () => {
    if (bulkManageBusy) return;
    setShowBulkManageModal(false);
  };

  const submitBulkUpdateSelected = async () => {
    setBulkManageError('');
    setError('');
    setSuccessMessage('');

    const ids = Array.from(selectedProductIds);
    if (ids.length === 0) {
      setBulkManageError('Select at least one product.');
      return;
    }

    const payload = {};
    const categoryVal = (bulkManageFields.Category || '').trim();
    if (categoryVal) payload.Category = categoryVal;

    const priceRaw = bulkManageFields.Price;
    if (priceRaw !== '' && priceRaw !== null && typeof priceRaw !== 'undefined') {
      const priceNum = Number(priceRaw);
      if (Number.isNaN(priceNum) || priceNum <= 0) {
        setBulkManageError('Price must be a number greater than 0.');
        return;
      }
      payload.Price = String(priceNum);
    }

    const stockRaw = bulkManageFields.Stock;
    if (stockRaw !== '' && stockRaw !== null && typeof stockRaw !== 'undefined') {
      const stockNum = parseInt(stockRaw, 10);
      if (Number.isNaN(stockNum) || stockNum < 1) {
        setBulkManageError('Stock must be an integer ≥ 1.');
        return;
      }
      payload.Stock = stockNum;
    }

    if (Object.keys(payload).length === 0) {
      setBulkManageError('Enter at least one field to update (Category, Price, or Stock).');
      return;
    }

    setBulkManageBusy(true);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      let okCount = 0;
      const failures = [];
      for (const productId of ids) {
        try {
          const resp = await fetch(`${API_BASE_URL}/admin/products/${productId}`, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

          const json = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            failures.push(json?.error || `Failed (${resp.status})`);
          } else {
            okCount += 1;
          }
        } catch (innerErr) {
          failures.push(innerErr?.message || 'Failed');
        }
      }

      const total = ids.length;
      if (okCount > 0) {
        setSuccessMessage(`Bulk update complete: ${okCount}/${total} updated.`);
      }
      if (failures.length > 0) {
        setBulkManageError(`Some updates failed (${failures.length}/${total}). Check console/logs for details.`);
        console.error('Bulk update failures:', failures);
      }
      closeBulkManage();
      clearSelectedProducts();
      fetchProducts();
    } catch (err) {
      console.error('Bulk update selected error:', err);
      setBulkManageError(err?.message || 'Bulk update failed');
    } finally {
      setBulkManageBusy(false);
    }
  };

  const deleteSelectedProducts = async () => {
    setBulkManageError('');
    setError('');
    setSuccessMessage('');

    const ids = Array.from(selectedProductIds);
    if (ids.length === 0) {
      setBulkManageError('Select at least one product.');
      return;
    }
    if (!window.confirm(`Delete ${ids.length} selected product(s)? This cannot be undone.`)) {
      return;
    }

    setBulkManageBusy(true);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      let okCount = 0;
      const failures = [];
      for (const productId of ids) {
        try {
          const resp = await fetch(`${API_BASE_URL}/admin/products/${productId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!resp.ok) {
            failures.push(`Failed (${resp.status})`);
          } else {
            okCount += 1;
          }
        } catch (innerErr) {
          failures.push(innerErr?.message || 'Failed');
        }
      }

      const total = ids.length;
      if (okCount > 0) {
        setSuccessMessage(`Bulk delete complete: ${okCount}/${total} deleted.`);
      }
      if (failures.length > 0) {
        setBulkManageError(`Some deletes failed (${failures.length}/${total}). Check console/logs for details.`);
        console.error('Bulk delete failures:', failures);
      }
      clearSelectedProducts();
      fetchProducts();
    } catch (err) {
      console.error('Bulk delete selected error:', err);
      setBulkManageError(err?.message || 'Bulk delete failed');
    } finally {
      setBulkManageBusy(false);
    }
  };

  const deleteAllProducts = async () => {
    setBulkManageError('');
    setError('');
    setSuccessMessage('');

    if (products.length === 0) {
      setBulkManageError('No products to delete.');
      return;
    }
    if (!window.confirm(`Delete ALL products (${products.length})? This cannot be undone.`)) {
      return;
    }
    const confirmText = window.prompt('Type DELETE ALL to confirm deleting every product.');
    if (confirmText !== 'DELETE ALL') {
      setBulkManageError('Delete all canceled.');
      return;
    }

    setBulkManageBusy(true);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      let okCount = 0;
      const failures = [];
      for (const p of products) {
        const productId = p.ProductID;
        if (!productId) continue;
        try {
          const resp = await fetch(`${API_BASE_URL}/admin/products/${productId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!resp.ok) {
            failures.push(`Failed (${resp.status})`);
          } else {
            okCount += 1;
          }
        } catch (innerErr) {
          failures.push(innerErr?.message || 'Failed');
        }
      }

      if (okCount > 0) {
        setSuccessMessage(`Delete all complete: ${okCount}/${products.length} deleted.`);
      }
      if (failures.length > 0) {
        setBulkManageError(`Some deletes failed (${failures.length}/${products.length}). Check console/logs for details.`);
        console.error('Delete all failures:', failures);
      }
      clearSelectedProducts();
      fetchProducts();
    } catch (err) {
      console.error('Delete all error:', err);
      setBulkManageError(err?.message || 'Delete all failed');
    } finally {
      setBulkManageBusy(false);
    }
  };

  const closeBulkUpload = () => {
    if (bulkBusy) return;
    setShowBulkModal(false);
  };

  const applyBulkCategoryToAll = (categoryValue) => {
    setBulkCategory(categoryValue);
    setBulkRows((prev) =>
      prev.map((row) => {
        if (row.categoryTouched) return row;
        return { ...row, Category: categoryValue };
      })
    );
  };

  const handleBulkFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    setBulkError('');

    if (!bulkCategory.trim()) {
      setBulkError('Please select a category before uploading.');
      return;
    }

    if (files.length === 0) return;
    if (files.length > BULK_MAX_ITEMS) {
      setBulkError(`Max ${BULK_MAX_ITEMS} images per batch for now.`);
      return;
    }

    const tooLarge = files.find((f) => (f?.size || 0) > BULK_MAX_IMAGE_BYTES);
    if (tooLarge) {
      setBulkError(`File too large: ${tooLarge.name}. Keep images under ${Math.round(BULK_MAX_IMAGE_BYTES / (1024 * 1024))}MB for now.`);
      return;
    }

    setBulkBusy(true);
    try {
      const dataUrls = await Promise.all(files.map(_readFileAsDataUrl));
      const colors = await Promise.all(files.map(_computeDominantColorName));

      const totalChars = dataUrls.reduce((sum, val) => sum + (typeof val === 'string' ? val.length : 0), 0);
      if (totalChars > BULK_MAX_TOTAL_DATAURL_CHARS) {
        setBulkError(
          `Selected images are too large to upload in one batch right now. Try fewer images or smaller files (limit ~${Math.round(
            BULK_MAX_TOTAL_DATAURL_CHARS / 1_000_000
          )}MB total).`
        );
        return;
      }

      const nextRows = files.map((file, idx) => {
        const imageDataUrl = dataUrls[idx] || '';
        const colorHint = colors[idx] || '';
        return {
          id: `${Date.now()}-${idx}-${file.name}`,
          file,
          previewUrl: URL.createObjectURL(file),
          imageDataUrl,
          colorHint,
          imageType: _extractImageTypeFromDataUrl(imageDataUrl),
          imageFilename: file.name || `image-${idx}.jpg`,
          ProductName: 'Generating...',
          Description: 'Generating...',
          Price: '',
          Stock: 1,
          Category: bulkCategory,
          categoryTouched: false,
          status: 'generating',
          error: '',
          fieldErrors: {},
        };
      });

      setBulkRows(nextRows);

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const analyzeResp = await fetch(`${API_BASE_URL}/admin/products/bulk/analyze`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          categoryHint: bulkCategory,
          images: nextRows.map((r) => ({
            image: r.imageDataUrl,
            filename: r.imageFilename,
            colorHint: r.colorHint,
          })),
        }),
      });

      const analyzeJson = await analyzeResp.json().catch(() => ({}));
      if (!analyzeResp.ok) {
        throw new Error(analyzeJson?.error || `Analyze failed (${analyzeResp.status})`);
      }

      const results = Array.isArray(analyzeJson?.results) ? analyzeJson.results : [];

      setBulkRows((prev) =>
        prev.map((row, idx) => {
          const res = results.find((r) => r.index === idx) || null;
          if (!res) {
            return { ...row, ProductName: '', Description: '', status: 'ready' };
          }
          if (!res.ok) {
            return {
              ...row,
              ProductName: '',
              Description: '',
              status: 'ready',
              error: res.error || 'Analyze failed',
            };
          }
          return {
            ...row,
            ProductName: res.name || '',
            Description: res.description || '',
            status: 'ready',
            error: '',
          };
        })
      );
    } catch (err) {
      console.error('Bulk upload analyze error:', err);
      setBulkError(err.message || 'Failed to analyze images');
    } finally {
      setBulkBusy(false);
      e.target.value = '';
    }
  };

  const updateBulkRow = (rowId, patch) => {
    setBulkRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        return { ...r, ...patch };
      })
    );
  };

  const removeBulkRow = (rowId) => {
    setBulkRows((prev) => {
      const target = prev.find((r) => r.id === rowId);
      if (target?.previewUrl) {
        try {
          URL.revokeObjectURL(target.previewUrl);
        } catch (_) {
          // ignore
        }
      }
      return prev.filter((r) => r.id !== rowId);
    });
  };

  const submitBulkUpdateAll = async () => {
    setBulkError('');
    setError('');
    setSuccessMessage('');

    if (bulkRows.length === 0) {
      setBulkError('Add at least one image.');
      return;
    }

    const validated = bulkRows.map((row) => ({
      id: row.id,
      errors: _validateBulkRow(row),
    }));

    const hasAnyErrors = validated.some((v) => Object.keys(v.errors).length > 0);
    if (hasAnyErrors) {
      setBulkRows((prev) =>
        prev.map((row) => {
          const v = validated.find((x) => x.id === row.id);
          return { ...row, fieldErrors: v ? v.errors : {}, status: 'ready' };
        })
      );
      setBulkError('Please fix the highlighted fields before updating.');
      return;
    }

    setBulkBusy(true);
    setBulkRows((prev) => prev.map((r) => ({ ...r, status: 'submitting', error: '' })));

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const payload = {
        products: bulkRows.map((r) => ({
          ProductName: r.ProductName,
          Description: r.Description,
          Price: r.Price,
          Category: r.Category,
          Stock: r.Stock,
          image: r.imageDataUrl,
          imageType: r.imageType,
          imageFilename: r.imageFilename,
        })),
      };

      const resp = await fetch(`${API_BASE_URL}/admin/products/bulk`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(json?.error || `Bulk update failed (${resp.status})`);
      }

      const results = Array.isArray(json?.results) ? json.results : [];
      setBulkRows((prev) =>
        prev.map((row, idx) => {
          const res = results.find((r) => r.index === idx);
          if (!res) return { ...row, status: 'error', error: 'Missing result' };
          if (!res.ok) return { ...row, status: 'error', error: res.error || 'Failed' };
          return { ...row, status: 'success', error: '' };
        })
      );

      const okCount = results.filter((r) => r.ok).length;
      setSuccessMessage(`Bulk create complete: ${okCount}/${bulkRows.length} products created.`);
      fetchProducts();
    } catch (err) {
      console.error('Bulk update error:', err);
      setBulkError(err.message || 'Failed to bulk create products');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const checkAuth = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const session = await Auth.currentSession();
      const groups = session.getIdToken().payload['cognito:groups'] || [];

      if (!groups.includes('Admins')) {
        navigate('/admin/login');
        return;
      }

      setUsername(user.username || user.attributes?.email || 'Admin');
      const token = session.getIdToken().getJwtToken();
      fetchThemeCategories(token);
      loadProductSettings(token);
      fetchProducts();
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/admin/login');
    }
  };

  const loadProductSettings = async (token) => {
    if (!API_BASE_URL) return;
    setProductSettingsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/general`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Failed to load settings (${res.status})`);
      }
      const settings = data?.settings && typeof data.settings === 'object' ? data.settings : {};
      const features = settings?.features && typeof settings.features === 'object' ? settings.features : {};
      setMultiQuantityEnabled(Boolean(features?.multi_quantity_enabled));
      const maxAllowed = Number(features?.max_quantity_allowed);
      setMaxQuantityAllowed(Number.isFinite(maxAllowed) && maxAllowed > 0 ? Math.floor(maxAllowed) : 10);
      setAutoInventoryUpdateEnabled(Boolean(features?.auto_inventory_update_enabled ?? true));
    } catch (e) {
      console.error('Error loading product settings:', e);
      setMultiQuantityEnabled(false);
      setMaxQuantityAllowed(10);
      setAutoInventoryUpdateEnabled(true);
    } finally {
      setProductSettingsLoading(false);
    }
  };

  const saveProductSettings = async () => {
    setError('');
    setSuccessMessage('');
    if (!API_BASE_URL) {
      setError('Admin API base URL is not configured.');
      return;
    }

    try {
      setProductSettingsLoading(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/general`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settings: {
            features: {
              multi_quantity_enabled: Boolean(multiQuantityEnabled),
              max_quantity_allowed: Number.isFinite(Number(maxQuantityAllowed)) && Number(maxQuantityAllowed) > 0
                ? Math.floor(Number(maxQuantityAllowed))
                : 10,
              auto_inventory_update_enabled: Boolean(autoInventoryUpdateEnabled),
            },
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Failed to save settings (${res.status})`);
      }

      const settings = data?.settings && typeof data.settings === 'object' ? data.settings : {};
      const features = settings?.features && typeof settings.features === 'object' ? settings.features : {};
      setMultiQuantityEnabled(Boolean(features?.multi_quantity_enabled));
      const maxAllowed = Number(features?.max_quantity_allowed);
      setMaxQuantityAllowed(Number.isFinite(maxAllowed) && maxAllowed > 0 ? Math.floor(maxAllowed) : 10);
      setAutoInventoryUpdateEnabled(Boolean(features?.auto_inventory_update_enabled ?? true));

      setSuccessMessage('Product settings saved.');
      setShowProductSettings(false);
    } catch (e) {
      console.error('Save product settings error:', e);
      setError(e?.message || 'Failed to save product settings');
    } finally {
      setProductSettingsLoading(false);
    }
  };

  const fetchThemeCategories = async (token) => {
    if (!API_BASE_URL) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/theme`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to load theme categories (${response.status})`);
      }
      const data = await response.json();
      const fromTheme = Array.isArray(data?.theme?.productCategories) ? data.theme.productCategories : [];
      setThemeCategories(fromTheme);
    } catch (themeError) {
      console.error('Error fetching theme categories:', themeError);
      setThemeCategories([]);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(
        `${process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL}/admin/products`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFileName(file.name || '');
      const reader = new FileReader();
      reader.onloadend = () => {
        // Store both preview and full data URL for backend upload
        setImagePreview(reader.result);
        setImageDataUrl(reader.result || '');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAltImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Append to existing files
      setAltImageFiles(prev => [...prev, ...files]);
      
      const newPreviews = [];
      const newDataUrls = [];
      let loadedCount = 0;

      files.forEach((file, index) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews[index] = reader.result;
          newDataUrls[index] = reader.result;
          loadedCount++;
          
          if (loadedCount === files.length) {
            setAltImagePreviews(prev => [...prev, ...newPreviews]);
            setAltImageDataUrls(prev => [...prev, ...newDataUrls]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeAltImage = (index) => {
    const preview = altImagePreviews[index];
    
    // Check if this is an existing image (URL) or a new upload (data URL)
    if (preview && preview.startsWith('http')) {
      // Remove from existing images
      setExistingAltImages(prev => prev.filter(url => url !== preview));
    } else {
      // Remove from new uploads - find the index in the new uploads array
      const newUploadIndex = altImageDataUrls.findIndex(dataUrl => dataUrl === preview);
      if (newUploadIndex !== -1) {
        setAltImageDataUrls(prev => prev.filter((_, i) => i !== newUploadIndex));
        setAltImageFiles(prev => prev.filter((_, i) => i !== newUploadIndex));
      }
    }
    
    // Always remove from preview
    setAltImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      ProductName: '',
      Description: '',
      Price: '',
      Category: '',
      Stock: 0,
      ImageURL: '',
      AllImages: []
    });
    setSaleEnabled(false);
    setSaleMode('percent');
    setSalePercent('');
    setSalePrice('');
    setSyncSquareEnabled(false);
    setImagePreview('');
    setImageDataUrl('');
    setImageFileName('');
    setExistingAltImages([]);
    setAltImageFiles([]);
    setAltImagePreviews([]);
    setAltImageDataUrls([]);
    setEditingProduct(null);
    setShowForm(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // Build JSON payload. Image (if selected) is sent as base64 data URL.
      const toNumberOrNull = (value) => {
        if (value === null || typeof value === 'undefined') return null;
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        const raw = String(value || '').trim();
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      };

      const round2 = (n) => Math.round(n * 100) / 100;

      const basePriceNum = toNumberOrNull(formData.Price);
      if (basePriceNum === null || basePriceNum <= 0) {
        throw new Error('Price must be a number greater than 0.');
      }

      let sellingPriceNum = basePriceNum;
      let compareAtNum = null;

      if (saleEnabled) {
        compareAtNum = basePriceNum;
        if (saleMode === 'percent') {
          const pct = toNumberOrNull(salePercent);
          if (pct === null || pct <= 0 || pct > 95) {
            throw new Error('Percent off must be between 1 and 95.');
          }
          sellingPriceNum = round2(compareAtNum * (1 - pct / 100));
        } else {
          const sp = toNumberOrNull(salePrice);
          if (sp === null || sp <= 0) {
            throw new Error('Sale price must be a number greater than 0.');
          }
          sellingPriceNum = round2(sp);
        }

        if (!(sellingPriceNum < compareAtNum)) {
          throw new Error('Sale price must be less than the regular price.');
        }
      }

      const payload = {
        ProductName: formData.ProductName,
        Description: formData.Description,
        Price: String(sellingPriceNum),
        Category: formData.Category,
        Stock: formData.Stock,
        ImageURL: formData.ImageURL,
        ...(syncSquareEnabled ? { syncSquare: true } : {}),
        ...(saleEnabled ? { CompareAtPrice: String(compareAtNum) } : {}),
      };

      // If editing and toggling sale off, explicitly clear CompareAtPrice.
      if (editingProduct && !saleEnabled) {
        const existingCompareAt = String(editingProduct?.CompareAtPrice || '').trim();
        const existingPrice = toNumberOrNull(editingProduct?.Price);
        const existingCompareAtNum = toNumberOrNull(existingCompareAt);
        if (existingCompareAtNum !== null && existingPrice !== null && existingCompareAtNum > existingPrice) {
          payload.CompareAtPrice = null;
        }
      }

      // For editing: send existing images to keep
      if (editingProduct && existingAltImages.length > 0) {
        payload.existingAltImages = existingAltImages;
      }
      
      // Add new AltImages if we have uploaded files
      if (altImageDataUrls.length > 0) {
        payload.altImages = altImageDataUrls.map((dataUrl, index) => {
          const match = dataUrl.match(/^data:image\/(.+);base64,/);
          return {
            data: dataUrl,
            type: match && match[1] ? match[1] : 'jpg',
            filename: altImageFiles[index]?.name || `alt-image-${index}.jpg`
          };
        });
      }

      if (imageDataUrl) {
        payload.image = imageDataUrl;
        // Extract file extension from data URL: data:image/jpeg;base64,...
        const match = imageDataUrl.match(/^data:image\/(.+);base64,/);
        if (match && match[1]) {
          payload.imageType = match[1];
        }
        if (imageFileName) {
          payload.imageFilename = imageFileName;
        }
      }

      const url = editingProduct
        ? `${process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL}/admin/products/${editingProduct.ProductID}`
        : `${process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL}/admin/products`;

      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const text = await response.text();
      let result = {};
      try {
        result = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('Failed to parse admin product response:', parseError, text);
        throw new Error('Invalid response from server while saving product');
      }

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save product');
      }

      setSuccessMessage(
        editingProduct ? 'Product updated successfully!' : 'Product created successfully!'
      );
      // Scroll to top so user sees the success message and top of the list
      window.scrollTo({ top: 0, behavior: 'smooth' });
      resetForm();
      fetchProducts();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving product:', error);
      setError(error.message || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);

    const toNumberOrNull = (value) => {
      if (value === null || typeof value === 'undefined') return null;
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      const raw = String(value || '').trim();
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };

    const existingPriceNum = toNumberOrNull(product?.Price);
    const compareAtRaw = String(product?.CompareAtPrice || '').trim();
    const compareAtNum = toNumberOrNull(compareAtRaw);
    const isOnSale =
      existingPriceNum !== null &&
      compareAtNum !== null &&
      compareAtNum > 0 &&
      compareAtNum > existingPriceNum;

    const inferredPercent = (() => {
      const dp = toNumberOrNull(product?.DiscountPercent);
      if (dp !== null && dp > 0) return String(Math.round(dp));
      if (!isOnSale) return '';
      const pct = Math.round(((compareAtNum - existingPriceNum) / compareAtNum) * 100);
      return pct > 0 ? String(pct) : '';
    })();

    setSaleEnabled(Boolean(isOnSale));
    setSaleMode('percent');
    setSalePercent(inferredPercent);
    setSalePrice(isOnSale ? String(existingPriceNum) : '');

    setFormData({
      ProductName: product.ProductName || '',
      Description: product.Description || '',
      // For sale items, show the regular price in the Price input.
      Price: isOnSale ? String(compareAtNum) : (product.Price || ''),
      Category: product.Category || '',
      Stock: product.Stock || 0,
      ImageURL: product.ImageURL || '',
      AllImages: product.AllImages || product.AltImages || []
    });
    
    // Load existing AltImages for preview
    const altImages = product.AltImages || product.AllImages || [];
    if (Array.isArray(altImages) && altImages.length > 0) {
      setExistingAltImages(altImages);
      setAltImagePreviews(altImages);
    } else {
      setExistingAltImages([]);
      setAltImagePreviews([]);
    }
    
    // Clear new uploads when editing
    setAltImageDataUrls([]);
    setAltImageFiles([]);
    
    setImagePreview(product.ImageURL || '');
    setShowForm(true);
    
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const baseUrl = `${process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL}/admin/products/${productId}`;
      const deleteUrl = syncSquareEnabled ? `${baseUrl}?syncSquare=true` : baseUrl;
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      setSuccessMessage('Product deleted successfully!');
      // Scroll to top so user sees the success message and top of the list
      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchProducts();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting product:', error);
      setError('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Admin Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-box"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Product Management</h1>
            <p>Admin Dashboard</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {successMessage}
          </div>
        )}

        {showProductSettings ? (
          <div className="bulk-modal-overlay" role="dialog" aria-modal="true">
            <div className="bulk-modal">
              <div className="bulk-modal-header bulk-modal-header-themed">
                <h2>Product Settings</h2>
                <button
                  type="button"
                  className="bulk-close"
                  onClick={() => setShowProductSettings(false)}
                  disabled={productSettingsLoading}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'center', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(multiQuantityEnabled)}
                      onChange={(e) => setMultiQuantityEnabled(e.target.checked)}
                      disabled={productSettingsLoading}
                    />
                    <span>
                      <strong>Multi quantity</strong> (global)
                      <div className="field-hint">Allow quantity editing on the Order Review page.</div>
                    </span>
                  </label>

                  {multiQuantityEnabled ? (
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
                      <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Max qty</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={maxQuantityAllowed}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const next = Number(raw);
                          if (raw === '') {
                            setMaxQuantityAllowed(1);
                            return;
                          }
                          if (!Number.isFinite(next)) return;
                          setMaxQuantityAllowed(Math.max(1, Math.floor(next)));
                        }}
                        disabled={productSettingsLoading}
                        style={{ width: 96, padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(15,23,42,0.2)' }}
                      />
                    </label>
                  ) : null}
                </div>

                <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(autoInventoryUpdateEnabled)}
                    onChange={(e) => setAutoInventoryUpdateEnabled(e.target.checked)}
                    disabled={productSettingsLoading}
                  />
                  <span>
                    <strong>Automatic inventory update</strong> (global)
                    <div className="field-hint">
                      When disabled, stock will not be decremented after checkout.
                    </div>
                  </span>
                </label>
              </div>

              <div className="bulk-actions" style={{ marginTop: 18 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowProductSettings(false)}
                  disabled={productSettingsLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-submit"
                  onClick={saveProductSettings}
                  disabled={productSettingsLoading}
                >
                  {productSettingsLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {showBulkModal && (
          <div className="bulk-modal-overlay" role="dialog" aria-modal="true">
            <div className="bulk-modal">
              <div className="bulk-modal-header bulk-modal-header-themed">
                <h2>Bulk Create Products</h2>
                <button
                  type="button"
                  className="bulk-close"
                  onClick={closeBulkUpload}
                  disabled={bulkBusy}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {bulkError && <div className="bulk-alert bulk-alert-error">{bulkError}</div>}

              <div className="bulk-controls">
                <div className="bulk-control">
                  <label>Category (applies to all by default) *</label>
                  <input
                    type="text"
                    list="categorySuggestions"
                    value={bulkCategory}
                    onChange={(e) => applyBulkCategoryToAll(e.target.value)}
                    placeholder="e.g. Scarves"
                    disabled={bulkBusy}
                  />
                </div>

                <div className="bulk-control">
                  <label>Upload images (max {BULK_MAX_ITEMS})</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleBulkFilesSelected}
                    disabled={bulkBusy}
                  />
                  <div className="bulk-hint">
                    Auto-generates name/description. You can edit everything before creating.
                  </div>
                </div>
              </div>

              {bulkRows.length > 0 && (
                <div className="bulk-table-wrap">
                  <table className="bulk-table">
                    <colgroup>
                      <col style={{ width: '70px' }} />
                      <col style={{ width: '24%' }} />
                      <col style={{ width: '28%' }} />
                      <col style={{ width: '92px' }} />
                      <col style={{ width: '86px' }} />
                      <col style={{ width: '16%' }} />
                      <col style={{ width: '96px' }} />
                      <col style={{ width: '44px' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Name *</th>
                        <th>Description *</th>
                        <th>Price *</th>
                        <th>Stock *</th>
                        <th>Category *</th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkRows.map((row) => (
                        <tr key={row.id} className={row.status === 'error' ? 'bulk-row-error' : ''}>
                          <td data-label="Image">
                            <img className="bulk-thumb" src={row.previewUrl} alt="" />
                          </td>
                          <td data-label="Name">
                            <input
                              type="text"
                              value={row.ProductName}
                              onChange={(e) => updateBulkRow(row.id, { ProductName: e.target.value })}
                              disabled={bulkBusy}
                              className={row.fieldErrors?.ProductName ? 'bulk-input-error' : ''}
                            />
                            {row.fieldErrors?.ProductName && <div className="bulk-field-error">{row.fieldErrors.ProductName}</div>}
                          </td>
                          <td data-label="Description">
                            <textarea
                              rows={2}
                              value={row.Description}
                              onChange={(e) => updateBulkRow(row.id, { Description: e.target.value })}
                              disabled={bulkBusy}
                              className={row.fieldErrors?.Description ? 'bulk-input-error' : ''}
                            />
                            {row.fieldErrors?.Description && <div className="bulk-field-error">{row.fieldErrors.Description}</div>}
                          </td>
                          <td className="bulk-cell-price" data-label="Price">
                            <input
                              type="number"
                              step="0.01"
                              value={row.Price}
                              onChange={(e) => updateBulkRow(row.id, { Price: e.target.value })}
                              disabled={bulkBusy}
                              className={row.fieldErrors?.Price ? 'bulk-input-error' : ''}
                            />
                            {row.fieldErrors?.Price && <div className="bulk-field-error">{row.fieldErrors.Price}</div>}
                          </td>
                          <td className="bulk-cell-stock" data-label="Stock">
                            <input
                              type="number"
                              value={row.Stock}
                              onChange={(e) => updateBulkRow(row.id, { Stock: e.target.value })}
                              disabled={bulkBusy}
                              className={row.fieldErrors?.Stock ? 'bulk-input-error' : ''}
                            />
                            {row.fieldErrors?.Stock && <div className="bulk-field-error">{row.fieldErrors.Stock}</div>}
                          </td>
                          <td data-label="Category">
                            <input
                              type="text"
                              list="categorySuggestions"
                              value={row.Category}
                              onChange={(e) => updateBulkRow(row.id, { Category: e.target.value, categoryTouched: true })}
                              disabled={bulkBusy}
                              className={row.fieldErrors?.Category ? 'bulk-input-error' : ''}
                            />
                            {row.fieldErrors?.Category && <div className="bulk-field-error">{row.fieldErrors.Category}</div>}
                          </td>
                          <td data-label="Status">
                            {row.status !== 'ready' && (
                              <div className={`bulk-status bulk-status-${row.status}`}>{row.status}</div>
                            )}
                            {row.error ? <div className="bulk-field-error">{row.error}</div> : null}
                          </td>
                          <td data-label="Remove">
                            <button
                              type="button"
                              className="bulk-remove"
                              onClick={() => removeBulkRow(row.id)}
                              disabled={bulkBusy}
                              title="Remove"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="bulk-actions">
                <button type="button" className="btn-secondary" onClick={closeBulkUpload} disabled={bulkBusy}>
                  Close
                </button>
                <button type="button" className="btn-submit" onClick={submitBulkUpdateAll} disabled={bulkBusy || bulkRows.length === 0}>
                  {bulkBusy ? 'Working...' : `Create All (${bulkRows.length})`}
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="admin-actions">
          <button
            onClick={() => setShowProductSettings(true)}
            className="btn-secondary"
            type="button"
          >
            <i className="fas fa-sliders-h"></i>
            Product settings
          </button>
          <button
            onClick={openBulkUpload}
            className="btn-secondary"
            type="button"
          >
            <i className="fas fa-upload"></i>
            Bulk Create
          </button>
          <div className="admin-controls">
            <button
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
              className="btn-primary"
            >
              <i className={showForm ? 'fas fa-times' : 'fas fa-plus'}></i>
              {showForm ? 'Cancel' : 'Add New Product'}
            </button>
            <button onClick={fetchProducts} className="btn-secondary">
              <i className="fas fa-sync-alt"></i>
              Refresh
            </button>
            <div className="admin-filter">
              <label htmlFor="categoryFilter" className="admin-filter-label">
                Category:
              </label>
              <select
                id="categoryFilter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                onKeyDown={(e) => clearOnKeyDown(e, () => setSelectedCategory(''))}
                className="admin-filter-select"
              >
                <option value="">All categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Product Form */}
        {showForm && (
          <div className="product-form-container">
            <h2>{editingProduct ? 'Edit Product' : 'Create New Product'}</h2>
            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    type="text"
                    value={formData.ProductName}
                    onChange={(e) => setFormData({ ...formData, ProductName: e.target.value })}
                    required
                    placeholder="Enter product name"
                  />
                </div>

                <div className="form-group">
                  <label>{saleEnabled ? 'Regular Price *' : 'Price *'}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.Price}
                    onChange={(e) => setFormData({ ...formData, Price: e.target.value })}
                    required
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={saleEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSaleEnabled(checked);
                      if (!checked) {
                        setSalePercent('');
                        setSalePrice('');
                        setSaleMode('percent');
                      } else {
                        if (!saleMode) setSaleMode('percent');
                      }
                    }}
                  />
                  On sale
                </label>
              </div>

              {saleEnabled ? (
                <div className="form-row" style={{ alignItems: 'end' }}>
                  <div className="form-group">
                    <label>Set sale by</label>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="radio"
                          name="saleMode"
                          value="percent"
                          checked={saleMode === 'percent'}
                          onChange={() => setSaleMode('percent')}
                        />
                        % off
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="radio"
                          name="saleMode"
                          value="price"
                          checked={saleMode === 'price'}
                          onChange={() => setSaleMode('price')}
                        />
                        Sale price
                      </label>
                    </div>
                  </div>

                  {saleMode === 'percent' ? (
                    <div className="form-group">
                      <label>% off</label>
                      <input
                        type="number"
                        value={salePercent}
                        onChange={(e) => setSalePercent(e.target.value)}
                        placeholder="10"
                        min="1"
                        max="95"
                      />
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Sale price</label>
                      <input
                        type="number"
                        step="0.01"
                        value={salePrice}
                        onChange={(e) => setSalePrice(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>
              ) : null}

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.Description}
                  onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                  required
                  rows="4"
                  placeholder="Enter product description"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <input
                    type="text"
                    list="categorySuggestions"
                    value={formData.Category}
                    onChange={(e) => setFormData({ ...formData, Category: e.target.value })}
                    required
                    placeholder="e.g. Scarves"
                  />
                  <datalist id="categorySuggestions">
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                  <p className="field-hint">
                    Start typing to reuse an existing category or enter a brand-new one.
                  </p>
                </div>

                <div className="form-group">
                  <label>Stock</label>
                  <input
                    type="number"
                    value={formData.Stock}
                    onChange={(e) => setFormData({ ...formData, Stock: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Product Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>
                  Additional Images (Optional)
                  <span style={{ fontSize: '0.85em', color: '#666', marginLeft: '8px' }}>
                    Upload multiple images for carousel display
                  </span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleAltImagesChange}
                />
                {altImagePreviews.length > 0 && (
                  <div className="alt-images-preview">
                    {altImagePreviews.map((preview, index) => (
                      <div key={index} className="alt-image-item">
                        <img src={preview} alt={`Alt ${index + 1}`} />
                        <button
                          type="button"
                          onClick={() => removeAltImage(index)}
                          className="remove-alt-image"
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <label className="inline-toggle">
                  <input
                    type="checkbox"
                    checked={syncSquareEnabled}
                    onChange={(e) => setSyncSquareEnabled(e.target.checked)}
                  />
                  <span>Sync to Square on save/delete</span>
                </label>
                <button type="submit" disabled={submitting} className="btn-submit">
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {editingProduct ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </>
                  )}
                </button>
                <button type="button" onClick={resetForm} className="btn-cancel">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Products List */}
        <div className="products-list-container">
          <div className="products-header">
            <h2>
              Products ({visibleProducts.length}
              {selectedCategory ? ` in ${selectedCategory}` : ''})
            </h2>
            <button onClick={syncSquareCatalog} className="btn-secondary" disabled={squareSyncBusy}>
              <i className="fas fa-cloud-upload-alt"></i>
              {squareSyncBusy ? 'Syncing POS...' : 'Sync POS Catalog'}
            </button>
          </div>
          <div className="bulk-manage-bar">
            <div className="bulk-manage-left">
              <label className="bulk-manage-selectall">
                <input
                  type="checkbox"
                  checked={visibleProducts.length > 0 && selectedProductIds.size === visibleProducts.length}
                  onChange={(e) => (e.target.checked ? selectAllVisibleProducts() : clearSelectedProducts())}
                  disabled={bulkManageBusy || visibleProducts.length === 0}
                />
                Select all
              </label>
              <div className="bulk-manage-count">Selected: {selectedProductIds.size}</div>
            </div>
            <div className="bulk-manage-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={openBulkManage}
                disabled={bulkManageBusy || selectedProductIds.size === 0}
              >
                Bulk Update
              </button>
              <button
                type="button"
                className="btn-delete"
                onClick={deleteSelectedProducts}
                disabled={bulkManageBusy || selectedProductIds.size === 0}
              >
                Delete Selected
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={deleteAllProducts}
                disabled={bulkManageBusy || products.length === 0}
              >
                Delete All
              </button>
            </div>
          </div>

          {bulkManageError && <div className="alert alert-error">{bulkManageError}</div>}
          {visibleProducts.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-box-open"></i>
              <p>No products found</p>
            </div>
          ) : (
            <div className="products-grid">
              {visibleProducts.map((product) => (
                <div key={product.ProductID} className="product-card">
                  <label className="product-select">
                    <input
                      type="checkbox"
                      checked={selectedProductIds.has(product.ProductID)}
                      onChange={() => toggleProductSelection(product.ProductID)}
                      disabled={bulkManageBusy}
                    />
                  </label>
                  <div className="product-image">
                    {Boolean(
                      String(product?.SquareCatalogItemId || '').trim() &&
                        String(product?.SquareCatalogVariationId || '').trim()
                    ) ? (
                      <span className="product-sync-overlay">POS: Synced</span>
                    ) : null}
                    {product.ImageURL ? (
                      <img 
                        src={product.ImageURL} 
                        alt={product.ProductName}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="no-image" style={{ display: product.ImageURL ? 'none' : 'flex' }}>
                      <i className="fas fa-image"></i>
                    </div>
                  </div>
                  <div className="product-details">
                    <h3>{product.ProductName}</h3>
                    <p className="product-description">{product.Description}</p>
                    <div className="product-meta">
                      {(() => {
                        const price = product?.Price;
                        const compareAt = product?.CompareAtPrice;
                        const dp = product?.DiscountPercent;
                        const priceNum = Number(String(price || '').replace(/[^0-9.]/g, ''));
                        const compareNum = Number(String(compareAt || '').replace(/[^0-9.]/g, ''));
                        const isSale =
                          Number.isFinite(priceNum) &&
                          Number.isFinite(compareNum) &&
                          compareNum > 0 &&
                          compareNum > priceNum;
                        if (!isSale) {
                          return <span className="product-price">${product.Price}</span>;
                        }
                        const pct = Number.isFinite(Number(dp)) ? Math.round(Number(dp)) : Math.round(((compareNum - priceNum) / compareNum) * 100);
                        return (
                          <span className="product-price">
                            <span style={{ fontWeight: 800 }}>${price}</span>
                            <span style={{ marginLeft: 8, textDecoration: 'line-through', color: '#f97316', fontWeight: 900 }}>${compareAt}</span>
                            {pct > 0 ? <span style={{ marginLeft: 8, fontWeight: 800, opacity: 0.9 }}>Sale: {pct}% off</span> : null}
                          </span>
                        );
                      })()}
                      <span className="product-category">{product.Category}</span>
                    </div>
                    <div className="product-meta">
                      <span className="product-stock">
                        <i className="fas fa-boxes"></i>
                        Stock: {product.Stock}
                      </span>
                    </div>
                  </div>
                  <div className="product-actions">
                    <button onClick={() => handleEdit(product)} className="btn-edit">
                      <i className="fas fa-edit"></i>
                      Edit
                    </button>
                    <button onClick={() => handleDelete(product.ProductID)} className="btn-delete">
                      <i className="fas fa-trash"></i>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showBulkManageModal && (
          <div className="bulk-modal-overlay" role="dialog" aria-modal="true">
            <div className="bulk-modal bulk-manage-modal">
              <div className="bulk-modal-header bulk-modal-header-themed">
                <h2>Bulk Update Selected</h2>
                <button
                  type="button"
                  className="bulk-close"
                  onClick={closeBulkManage}
                  disabled={bulkManageBusy}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {bulkManageError && <div className="bulk-alert bulk-alert-error">{bulkManageError}</div>}

              <div className="bulk-controls" style={{ marginBottom: 12 }}>
                <div className="bulk-control" style={{ gridColumn: '1 / -1' }}>
                  <label>Action</label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="radio"
                        name="bulkManageMode"
                        value="update"
                        checked={bulkManageMode === 'update'}
                        onChange={() => setBulkManageMode('update')}
                        disabled={bulkManageBusy}
                      />
                      Update fields
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="radio"
                        name="bulkManageMode"
                        value="sale"
                        checked={bulkManageMode === 'sale'}
                        onChange={() => setBulkManageMode('sale')}
                        disabled={bulkManageBusy}
                      />
                      Apply sale
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="radio"
                        name="bulkManageMode"
                        value="clear_sale"
                        checked={bulkManageMode === 'clear_sale'}
                        onChange={() => setBulkManageMode('clear_sale')}
                        disabled={bulkManageBusy}
                      />
                      Clear sale
                    </label>
                  </div>
                </div>
              </div>

              <div className="bulk-controls">
                <div className="bulk-control">
                  <label>Category</label>
                  <input
                    type="text"
                    list="categorySuggestions"
                    value={bulkManageFields.Category}
                    onChange={(e) => setBulkManageFields((p) => ({ ...p, Category: e.target.value }))}
                    placeholder="(leave blank to keep existing)"
                    disabled={bulkManageBusy}
                  />
                </div>

                {bulkManageMode === 'update' ? (
                  <div className="bulk-control">
                    <label>Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bulkManageFields.Price}
                      onChange={(e) => setBulkManageFields((p) => ({ ...p, Price: e.target.value }))}
                      placeholder="(leave blank to keep existing)"
                      disabled={bulkManageBusy}
                    />
                  </div>
                ) : null}

                <div className="bulk-control">
                  <label>Stock</label>
                  <input
                    type="number"
                    value={bulkManageFields.Stock}
                    onChange={(e) => setBulkManageFields((p) => ({ ...p, Stock: e.target.value }))}
                    placeholder="(leave blank to keep existing)"
                    disabled={bulkManageBusy}
                  />
                </div>

                {bulkManageMode === 'sale' ? (
                  <div className="bulk-control" style={{ gridColumn: '1 / -1' }}>
                    <label>Sale type</label>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="radio"
                          name="bulkSaleMode"
                          value="percent"
                          checked={bulkSaleMode === 'percent'}
                          onChange={() => setBulkSaleMode('percent')}
                          disabled={bulkManageBusy}
                        />
                        % off
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="radio"
                          name="bulkSaleMode"
                          value="price"
                          checked={bulkSaleMode === 'price'}
                          onChange={() => setBulkSaleMode('price')}
                          disabled={bulkManageBusy}
                        />
                        Sale price
                      </label>
                    </div>

                    {bulkSaleMode === 'percent' ? (
                      <div style={{ marginTop: 10 }}>
                        <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>
                          Sale: % off (skips products already on sale)
                        </label>
                        <input
                          type="number"
                          value={bulkSalePercent}
                          onChange={(e) => setBulkSalePercent(e.target.value)}
                          placeholder="10"
                          min="1"
                          max="95"
                          disabled={bulkManageBusy}
                        />
                      </div>
                    ) : (
                      <div style={{ marginTop: 10 }}>
                        <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>
                          Sale: set sale price (skips products already on sale)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={bulkSalePrice}
                          onChange={(e) => setBulkSalePrice(e.target.value)}
                          placeholder="9.99"
                          min="0.01"
                          disabled={bulkManageBusy}
                        />
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="bulk-actions">
                <button type="button" className="btn-secondary" onClick={closeBulkManage} disabled={bulkManageBusy}>
                  Close
                </button>
                <button type="button" className="btn-submit" onClick={submitBulkManageAction} disabled={bulkManageBusy}>
                  {bulkManageBusy
                    ? 'Working...'
                    : bulkManageMode === 'sale'
                    ? `Apply Sale (${selectedProductIds.size})`
                    : bulkManageMode === 'clear_sale'
                    ? `Clear Sale (${selectedProductIds.size})`
                    : `Apply Updates (${selectedProductIds.size})`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        className={`admin-scroll-top ${showScrollTop ? '' : 'hidden'}`}
        onClick={handleScrollTop}
        aria-label="Scroll to top"
        title="Scroll to top"
      >
        <i className="fas fa-arrow-up"></i>
      </button>
    </div>
  );
};

export default AdminDashboard;
